import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../../api';
import Icon, { IconName } from '../../design/Icon';
import { Sheet, fmtDateShort, fmtTime, todayStr, addDaysStr } from '../../design/atoms';
import TargetSheet from '../../plan/TargetSheet';
import {
  RECIPES, HORIZON_DAYS, WORKOUT_TYPES, fmtTarget,
  type RecipeDef, type TargetMap, type WorkoutType,
} from '../../plan/planShared';
import { picksForFocus, allExercises, type LibraryExercise } from '../../plan/exerciseLibrary';

/* SPEC 04 → 05 → 06 → 09: pick a shape · arrange days · pick exercises · saved.
   Dates are fixed throughout — arranging moves FOCUSES between days. */

interface DraftItem {
  key: string;
  exerciseName: string;
  workoutType: WorkoutType;
  target: TargetMap;
}

interface DraftDay {
  date: string;
  focus: string;
  isRest: boolean;
  /** Optional wall-clock start ("HH:MM"); null means "any time". */
  startTime: string | null;
  items: DraftItem[];
}

const RECIPE_ICONS: Record<string, IconName> = {
  rot: 'dumbbell', ppl: 'sliders', ul: 'up', full: 'user', hyb: 'flame', stn: 'medal', run: 'pin', blank: 'plus',
};

/** Session-time choices: every half hour from 05:00 to 22:00. */
const TIME_CHOICES = Array.from({ length: 35 }, (_, i) => {
  const mins = 5 * 60 + i * 30;
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${mins % 60 === 0 ? '00' : '30'}`;
});

const TYPE_ICONS: Record<string, IconName> = {
  strength: 'dumbbell', body: 'user', run: 'flame', erg: 'heart', circuit: 'spark', hold: 'clock', flow: 'leaf',
};

let keySeq = 0;
const nextKey = () => `d${++keySeq}`;

const toDraftItem = (e: LibraryExercise): DraftItem => ({
  key: nextKey(), exerciseName: e.name, workoutType: e.workoutType, target: { ...e.target },
});

/** Build the dated skeleton: starts tomorrow, focuses cycle the recipe's 7 slots. */
function buildDays(recipe: RecipeDef, horizon: number, startsOn: string): DraftDay[] {
  return Array.from({ length: horizon }, (_, i) => {
    const focus = recipe.days[i % 7];
    const isRest = focus === 'Rest';
    return {
      date: addDaysStr(startsOn, i),
      focus,
      isRest,
      startTime: null,
      items: isRest ? [] : picksForFocus(focus).map(toDraftItem),
    };
  });
}

export default function MemberPlanBuilderPage() {
  const { token } = useMemberAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [recipeKey, setRecipeKey] = useState('rot');
  const [horizon, setHorizon] = useState(7);
  const [days, setDays] = useState<DraftDay[]>([]);
  const [dayIndex, setDayIndex] = useState(0);
  const [editing, setEditing] = useState<{ dayIndex: number; itemKey: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  /** Open time sheet: a day index, or 'all' for every training day. */
  const [timeFor, setTimeFor] = useState<number | 'all' | null>(null);

  const startsOn = useMemo(() => addDaysStr(todayStr(), 1), []);
  const recipe = RECIPES.find(r => r.key === recipeKey)!;

  const goToArrange = () => {
    setDays(buildDays(recipe, horizon, startsOn));
    setStep(2);
  };

  /** Move a focus (with its items) up or down — dates stay put. */
  const moveFocus = (from: number, to: number) => {
    if (to < 0 || to >= days.length) return;
    setDays(prev => {
      // The session — focus, its time and its exercises — moves as one unit.
      const focuses = prev.map(d => ({ focus: d.focus, isRest: d.isRest, startTime: d.startTime, items: d.items }));
      const [moved] = focuses.splice(from, 1);
      focuses.splice(to, 0, moved);
      return prev.map((d, i) => ({ ...d, ...focuses[i] }));
    });
  };

  const updateItem = (di: number, key: string, target: TargetMap) =>
    setDays(prev => prev.map((d, i) =>
      i === di ? { ...d, items: d.items.map(it => (it.key === key ? { ...it, target } : it)) } : d
    ));

  const removeItem = (di: number, key: string) =>
    setDays(prev => prev.map((d, i) => (i === di ? { ...d, items: d.items.filter(it => it.key !== key) } : d)));

  const addItem = (di: number, e: LibraryExercise) =>
    setDays(prev => prev.map((d, i) => (i === di ? { ...d, items: [...d.items, toDraftItem(e)] } : d)));

  const shufflePicks = (di: number) =>
    setDays(prev => prev.map((d, i) => {
      if (i !== di) return d;
      const pool = picksForFocus(d.focus, 99);
      if (pool.length === 0) return d;
      const have = new Set(d.items.map(it => it.exerciseName));
      const fresh = pool.filter(e => !have.has(e.name));
      const rotated = [...fresh, ...pool].slice(0, 6);
      return { ...d, items: rotated.map(toDraftItem) };
    }));

  const applyToAllDays = (di: number) => {
    const source = days[di];
    setDays(prev => prev.map(d =>
      d.isRest || d.date === source.date
        ? d
        : { ...d, items: source.items.map(it => ({ ...it, key: nextKey() })) }
    ));
    toast.success('Applied to every training day');
  };

  /** Set (or clear) the session time for one day, or for every training day. */
  const setDayTime = (target: number | 'all', time: string | null) => {
    setDays(prev => prev.map((d, i) =>
      target === 'all' ? (d.isRest ? d : { ...d, startTime: time }) : i === target ? { ...d, startTime: time } : d
    ));
    if (target === 'all') {
      toast.success(time ? `Every training day set to ${fmtTime(time)}` : 'Times cleared');
    }
    setTimeFor(null);
  };

  const publish = async () => {
    if (!token || saving) return;
    setSaving(true);
    try {
      await mfetch(token, '/member/plan', {
        method: 'POST',
        body: {
          recipeKey,
          startsOn: days[0].date,
          horizonDays: horizon,
          days: days.map(d => ({
            date: d.date,
            focus: d.focus,
            isRest: d.isRest,
            startTime: d.startTime,
            items: d.items.map(it => ({
              exerciseName: it.exerciseName,
              workoutType: it.workoutType,
              target: it.target,
            })),
          })),
        },
      });
      setSaved(true);
    } catch (e) {
      toast.error(e instanceof MemberApiError ? e.message : 'Could not save your plan');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => ({
    workouts: days.filter(d => !d.isRest).length,
    exercises: days.reduce((n, d) => n + d.items.length, 0),
  }), [days]);

  const editingItem = editing ? days[editing.dayIndex]?.items.find(i => i.key === editing.itemKey) : null;

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = allExercises();
    return q ? pool.filter(e => e.name.toLowerCase().includes(q)).slice(0, 25) : pool.slice(0, 25);
  }, [search]);

  // ── Success (SPEC 09) ──────────────────────────────────────────────────────
  if (saved) {
    return (
      <div className="mpage">
        <div className="col" style={{ alignItems: 'center', gap: 14, paddingTop: 28 }}>
          <div className="ok">
            <i /><i />
            <b><Icon n="check" s={34} w={2.2} /></b>
          </div>
          <div className="col rise" style={{ alignItems: 'center', gap: 6 }}>
            <div className="dp dp-l">Your week is set</div>
            <div className="sub wrap" style={{ textAlign: 'center' }}>
              {fmtDateShort(days[0].date)} – {fmtDateShort(days[days.length - 1].date)} · {recipe.name.toLowerCase()}.
              Your bookings stayed exactly where they were.
            </div>
          </div>
        </div>

        <div className="card rise-2" style={{ padding: 16 }}>
          <div className="between">
            {[
              ['Workouts', String(totals.workouts)],
              ['Exercises', String(totals.exercises)],
              ['Days', String(horizon)],
            ].map(([l, v]) => (
              <div key={l} className="col" style={{ gap: 3 }}>
                <div className="lbl">{l}</div>
                <div className="num" style={{ fontSize: 18 }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="divider" style={{ margin: '14px 0 10px' }} />
          <div className="tiny wrap">
            Add an automation and unlogged workouts can roll forward on their own.
          </div>
        </div>

        <button className="btn gold block" onClick={() => navigate('/app/calendar')}>See it in my calendar</button>
        <button className="btn ghost block" onClick={() => navigate('/app/plan/rules')}>Review automations</button>
      </div>
    );
  }

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button
          className="icon-btn"
          aria-label="Back"
          onClick={() => (step === 1 ? navigate(-1) : setStep(step === 3 ? 2 : 1))}
        >
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="col" style={{ gap: 1, minWidth: 0 }}>
          <div className="dp dp-m">
            {step === 1 ? 'Plan your week' : step === 2 ? 'Arrange the days' : `${fmtDateShort(days[dayIndex].date)} · ${days[dayIndex].focus}`}
          </div>
          <div className="tiny">
            {step === 1 ? 'Step 1 of 3 · pick a shape'
              : step === 2 ? 'Step 2 of 3 · reorder the focuses'
              : 'Step 3 of 3 · top 6 picked for you'}
          </div>
        </div>
      </div>

      {/* ── Step 1 · pick a shape ─────────────────────────────────────────── */}
      {step === 1 && (
        <>
          <div className="hscroll">
            {HORIZON_DAYS.map(h => (
              <button key={h} className={`chip${horizon === h ? ' on' : ''}`} onClick={() => setHorizon(h)}>
                Next {h} days
              </button>
            ))}
          </div>

          <div className="col" style={{ gap: 10 }}>
            {RECIPES.map(r => {
              const on = r.key === recipeKey;
              return (
                <button
                  key={r.key}
                  className="card"
                  style={{
                    textAlign: 'left',
                    borderColor: on ? 'var(--ink)' : 'var(--line)',
                    background: on ? 'var(--tint2)' : 'var(--card)',
                  }}
                  onClick={() => setRecipeKey(r.key)}
                >
                  <div className="row" style={{ gap: 11 }}>
                    <div className="li gold"><Icon n={RECIPE_ICONS[r.key] ?? 'dumbbell'} s={17} c="var(--gold-2)" /></div>
                    <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                      <div className="row" style={{ gap: 7 }}>
                        <span className="mid trunc">{r.name}</span>
                        {r.tag && <span className="tag gold">{r.tag}</span>}
                      </div>
                      <div className="tiny trunc">{r.sub}</div>
                    </div>
                    {on && <Icon n="check" s={18} c="var(--gold-2)" />}
                  </div>
                  {on && (
                    <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                      {r.days.map((f, i) => (
                        <span key={i} className={`tag${f === 'Rest' ? '' : ' gold'}`}>{f}</span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <button className="btn gold block" onClick={goToArrange}>Continue</button>
        </>
      )}

      {/* ── Step 2 · arrange (dates fixed, focuses move) ──────────────────── */}
      {step === 2 && (
        <>
          <div className="tiny wrap">
            {fmtDateShort(days[0].date)} – {fmtDateShort(days[days.length - 1].date)} · dates stay put, only the focuses move
          </div>

          <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setTimeFor('all')}>
            <Icon n="clock" s={15} c="var(--ink)" />
            Same time for all days
          </button>

          <div className="col" style={{ gap: 8 }}>
            {days.map((d, i) => (
              <div
                key={d.date}
                className="card"
                style={{
                  padding: '11px 13px',
                  ...(d.isRest ? { background: 'var(--card-2)', border: '1.5px dashed var(--line-2)', boxShadow: 'none' } : {}),
                }}
              >
                <div className="row" style={{ gap: 11 }}>
                  <div className="col" style={{ gap: 0, alignItems: 'center', width: 38, flex: '0 0 38px' }}>
                    <div className="lbl">{fmtDateShort(d.date).slice(0, 3).toUpperCase()}</div>
                    <div className="num" style={{ fontSize: 15 }}>{Number(d.date.slice(8))}</div>
                  </div>
                  <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
                    <div className="mid trunc">{d.focus}</div>
                    <div className="tiny trunc">
                      {d.isRest ? 'Rest day' : `${d.items.length} exercise${d.items.length === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  {!d.isRest && (
                    <button className="chip" onClick={() => setTimeFor(i)}>
                      {d.startTime ? fmtTime(d.startTime) : 'Time'}
                    </button>
                  )}
                  <div className="row" style={{ gap: 4 }}>
                    <button className="icon-btn" aria-label="Move earlier" onClick={() => moveFocus(i, i - 1)} disabled={i === 0}>
                      <Icon n="up" s={16} c="var(--ink-2)" />
                    </button>
                    <button
                      className="icon-btn"
                      aria-label="Move later"
                      onClick={() => moveFocus(i, i + 1)}
                      disabled={i === days.length - 1}
                      style={{ transform: 'rotate(180deg)' }}
                    >
                      <Icon n="up" s={16} c="var(--ink-2)" />
                    </button>
                    <button
                      className="icon-btn"
                      aria-label="Edit exercises"
                      onClick={() => { setDayIndex(i); setStep(3); }}
                    >
                      <Icon n="right" s={16} c="var(--ink-2)" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button className="btn ghost block" onClick={() => navigate('/app/plan/rules')}>Add a rule</button>
          <button className="btn gold block" onClick={() => { setDayIndex(days.findIndex(d => !d.isRest) || 0); setStep(3); }}>
            Pick exercises
          </button>
        </>
      )}

      {/* ── Step 3 · exercises ────────────────────────────────────────────── */}
      {step === 3 && (
        <>
          <div className="hscroll">
            {days.map((d, i) => (
              <button key={d.date} className={`chip${i === dayIndex ? ' on' : ''}`} onClick={() => setDayIndex(i)}>
                {fmtDateShort(d.date).replace(/ \d+ /, ' ')} · {d.focus}
              </button>
            ))}
          </div>

          {days[dayIndex].isRest ? (
            <div className="card dashed" style={{ textAlign: 'center', padding: 24 }}>
              <div className="mid">Rest day</div>
              <div className="sub wrap" style={{ marginTop: 4 }}>Nothing to pick — recovery counts.</div>
            </div>
          ) : (
            <>
              <div className="hscroll">
                <button className="chip" onClick={() => shufflePicks(dayIndex)}>Shuffle picks</button>
                <button className="chip" onClick={() => setAdding(true)}>＋ Add exercise</button>
              </div>

              <div className="card" style={{ padding: '5px 16px' }}>
                {days[dayIndex].items.length === 0 ? (
                  <div className="sub wrap" style={{ padding: '14px 0' }}>Nothing picked yet — add an exercise.</div>
                ) : (
                  days[dayIndex].items.map(it => (
                    <div key={it.key} className="lrow">
                      <button
                        className="li gold"
                        aria-label={`Edit ${it.exerciseName}`}
                        onClick={() => setEditing({ dayIndex, itemKey: it.key })}
                      >
                        <Icon n={TYPE_ICONS[it.workoutType] ?? 'dumbbell'} s={16} c="var(--gold-2)" />
                      </button>
                      <button
                        className="col"
                        style={{ gap: 1, flex: 1, minWidth: 0, textAlign: 'left' }}
                        onClick={() => setEditing({ dayIndex, itemKey: it.key })}
                      >
                        <div className="mid trunc">{it.exerciseName}</div>
                        <div className="tiny trunc">
                          {fmtTarget(it.target)} · {WORKOUT_TYPES[it.workoutType]?.label}
                        </div>
                      </button>
                      <button
                        className="icon-btn"
                        aria-label={`Remove ${it.exerciseName}`}
                        onClick={() => removeItem(dayIndex, it.key)}
                      >
                        <Icon n="minus" s={16} c="var(--clay)" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <button className="btn ghost block" onClick={() => applyToAllDays(dayIndex)}>Apply to all days</button>
            </>
          )}

          <button className="btn gold block" disabled={saving} onClick={publish}>
            {saving ? 'Saving…' : `Save ${horizon}-day plan`}
          </button>
        </>
      )}

      {editingItem && editing && (
        <TargetSheet
          exerciseName={editingItem.exerciseName}
          workoutType={editingItem.workoutType}
          initial={editingItem.target}
          mode="target"
          onClose={() => setEditing(null)}
          onSave={target => {
            updateItem(editing.dayIndex, editing.itemKey, target);
            setEditing(null);
          }}
        />
      )}

      {adding && (
        <Sheet onClose={() => { setAdding(false); setSearch(''); }}>
          <div className="col" style={{ gap: 2 }}>
            <div className="lbl">Add exercise</div>
            <div className="dp dp-m">{days[dayIndex].focus}</div>
          </div>
          <input
            className="minput"
            placeholder="Search exercises"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="col" style={{ maxHeight: '46dvh', overflowY: 'auto' }}>
            {searchResults.map(e => (
              <button
                key={e.name}
                className="lrow"
                onClick={() => { addItem(dayIndex, e); setAdding(false); setSearch(''); }}
              >
                <div className="li gold"><Icon n={TYPE_ICONS[e.workoutType] ?? 'dumbbell'} s={16} c="var(--gold-2)" /></div>
                <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
                  <div className="mid trunc">{e.name}</div>
                  <div className="tiny trunc">{WORKOUT_TYPES[e.workoutType]?.label} · {fmtTarget(e.target)}</div>
                </div>
                <Icon n="plus" s={17} c="var(--gold-2)" />
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {timeFor !== null && (
        <Sheet onClose={() => setTimeFor(null)}>
          <div className="col" style={{ gap: 2 }}>
            <div className="lbl">Session time</div>
            <div className="dp dp-m">
              {timeFor === 'all'
                ? 'Every training day'
                : `${fmtDateShort(days[timeFor].date)} · ${days[timeFor].focus}`}
            </div>
          </div>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8, maxHeight: '46dvh', overflowY: 'auto' }}>
            {TIME_CHOICES.map(t => {
              const on = timeFor !== 'all' && days[timeFor].startTime === t;
              return (
                <button key={t} className={`chip${on ? ' on' : ''}`} onClick={() => setDayTime(timeFor, t)}>
                  {fmtTime(t)}
                </button>
              );
            })}
          </div>
          <button className="btn ghost block" onClick={() => setDayTime(timeFor, null)}>No set time</button>
        </Sheet>
      )}
    </div>
  );
}
